import { ColourNames } from 'tms-lib';
import { CodeParameters } from '../models/sales/code-parameters';
import { LineExceptionRules } from './line-exception-rules';

// Why an open sales order line looks stuck (Sales Order What's Happened, D-3 and D-15). Mirrors
// TMSWeb.Common.Constants.LineExceptionReasons: the API sends a CODE plus named parameters, because
// its responses are not translated server-side (D-9), and the words are chosen here in $localize.
// The two lists must stay in step. The same code is the audit's verdict code, worded separately
// by lineVerdictText. Stock numbers, statuses, warehouse and location names arrive as stored.
export enum LineExceptionReasons {
  // (a) params: status, days
  tooLongAtStatus = 'TooLongAtStatus',
  // (b) params: purchaseOrder, sheet
  madeBeforeCut = 'MadeBeforeCut',
  // (c) no params
  journeyClosedNothingDespatched = 'JourneyClosedNothingDespatched',
  // (d) params: status
  noLiveList = 'NoLiveList',
  // (d) params: list, listStatus
  deadList = 'DeadList',
  // (d) params: transfer, date (yyyy-MM-dd, the date it was raised)
  transferNotDeparted = 'TransferNotDeparted',
  // (e) params: stock, product, lineProduct
  productMismatch = 'ProductMismatch',
  // (e) params: stock, warehouse (where it is), expectedWarehouse
  warehouseMismatch = 'WarehouseMismatch',
  // (f) params: stock
  despatchedTwice = 'DespatchedTwice',
  // (f) params: despatched, ordered
  despatchedMoreThanOrdered = 'DespatchedMoreThanOrdered',
  // (f) params: invoiced, ordered
  invoicedMoreThanOrdered = 'InvoicedMoreThanOrdered',
  // (g1) params: stock, location, putAwayLocation (for the verdict)
  finishedGoodsAtCutting = 'FinishedGoodsAtCutting',
  // (g2) params: stock, location
  stagedNotAtCutting = 'StagedNotAtCutting',
  // (h) params: days (whole days past the line's delivery date) - Sales Order Board, D-11
  lateForDelivery = 'LateForDelivery',
  // Traffic Light step (Manufacturing added to the Exceptions list, D-1) params: stepId, step (the step's
  // Description as stored), colour (a ColourNames value, or blank), sequence, days. Not one of the rules
  // (a)-(h): the Sales Order Board gives each step its own tile (D-5). Never un-stickable (D-7).
  trafficLightStep = 'TrafficLightStep',
  // Audit verdict when no rule applies.
  noProblems = 'NoProblems',
}

// The reason as a person reads it - concise, naming the document or figure involved (requirement 9).
export function lineExceptionReasonText(reason: CodeParameters): string {
  const parameter = parameterReader(reason);

  switch (reason?.code) {
    case LineExceptionReasons.tooLongAtStatus:
      // The status is shown as stored ("Awaiting Manufacture"). Never "1 days": a status is only
      // flagged once it is older than its Stuck after (days), and 0 means never, so days is 2 or more.
      return $localize`${parameter('status')}:status: for ${parameter('days')}:days: days`;
    case LineExceptionReasons.madeBeforeCut:
      return $localize`Make order ${parameter('purchaseOrder')}:purchaseOrder: was received before its fabric was cut (sheet ${parameter('sheet')}:sheet:)`;
    case LineExceptionReasons.journeyClosedNothingDespatched:
      return $localize`Journey closed but nothing despatched`;
    case LineExceptionReasons.noLiveList:
      return $localize`${parameter('status')}:status: with no live picking list`;
    case LineExceptionReasons.deadList:
      return $localize`On ${parameter('listStatus')}:listStatus: list ${parameter('list')}:list:`;
    case LineExceptionReasons.transferNotDeparted:
      return $localize`Transfer ${parameter('transfer')}:transfer: not departed (raised ${displayDate(parameter('date'))}:date:)`;
    case LineExceptionReasons.productMismatch:
      return $localize`Allocated stock is a different product: ${parameter('stock')}:stock: is ${parameter('product')}:product:, not ${parameter('lineProduct')}:lineProduct:`;
    case LineExceptionReasons.warehouseMismatch:
      return $localize`Allocated stock is in a different warehouse: ${parameter('stock')}:stock: is in ${parameter('warehouse')}:warehouse:, not ${parameter('expectedWarehouse')}:expectedWarehouse:`;
    case LineExceptionReasons.despatchedTwice:
      return $localize`Despatched twice: ${parameter('stock')}:stock: is on two despatch items`;
    // The quantities arrive as invariant "0.###" strings and are shown as sent: wording them in the
    // user's number format needs FormatService, which these pure functions do not have.
    case LineExceptionReasons.despatchedMoreThanOrdered:
      return $localize`Despatched more than ordered (${parameter('despatched')}:despatched: of ${parameter('ordered')}:ordered:)`;
    case LineExceptionReasons.invoicedMoreThanOrdered:
      return $localize`Invoiced more than ordered (${parameter('invoiced')}:invoiced: of ${parameter('ordered')}:ordered:)`;
    case LineExceptionReasons.finishedGoodsAtCutting:
      return $localize`Finished goods held at Staged for Cutting: ${parameter('stock')}:stock: is in ${parameter('location')}:location:`;
    case LineExceptionReasons.stagedNotAtCutting:
      return $localize`Staged for Cutting, but ${parameter('stock')}:stock: is not at a cutting location (${parameter('location')}:location:)`;
    case LineExceptionReasons.lateForDelivery:
      // The days can be 1, and a $localize in a .ts file cannot pluralise (ui.md), so the count is
      // worded as a figure rather than "by 1 days".
      return $localize`Late for delivery (days late: ${parameter('days')}:days:)`;
    case LineExceptionReasons.trafficLightStep:
      // The step heading is the customer's own data, shown as stored and never translated (D-4); the
      // days are a figure for the same reason as (h)'s.
      return $localize`Traffic Light: ${parameter('step')}:step: (days waiting: ${parameter('days')}:days:)`;
    case LineExceptionReasons.noProblems:
      return $localize`No problems found`;
    default:
      // Anything unrecognised shows as sent rather than vanishing.
      return reason?.code ?? '';
  }
}

// Every reason on a line, for one grid cell (the Reason column on the tab and the list). Blank when none.
export function lineExceptionReasonsText(reasons: CodeParameters[] | undefined | null): string {
  return (reasons ?? []).map((reason: CodeParameters) => lineExceptionReasonText(reason)).join('; ');
}

// The Sales Order Line Exceptions list's own row colours (Derek, 28 Oct 2026, replacing the Order Line
// Status colour there). One colour per kind of reason - the rule letter of D-3 / D-15 - chosen from the
// CODE alone, so the days, figures and documents in the words never change it. A line with no reason
// is green. These are not a status table's colours, so they are fixed here rather than read from one.
const lineExceptionReasonColours: Record<string, ColourNames> = {
  // (a) at one status too long
  [LineExceptionReasons.tooLongAtStatus]: ColourNames.yellow,
  // (b) made before its fabric was cut
  [LineExceptionReasons.madeBeforeCut]: ColourNames.purple,
  // (c) journey closed, nothing despatched
  [LineExceptionReasons.journeyClosedNothingDespatched]: ColourNames.pink,
  // (d) the picking list or transfer has stalled
  [LineExceptionReasons.noLiveList]: ColourNames.orange,
  [LineExceptionReasons.deadList]: ColourNames.orange,
  [LineExceptionReasons.transferNotDeparted]: ColourNames.orange,
  // (e) the allocated stock does not match the line
  [LineExceptionReasons.productMismatch]: ColourNames.blue,
  [LineExceptionReasons.warehouseMismatch]: ColourNames.blue,
  // (f) despatched or invoiced more than there should be
  [LineExceptionReasons.despatchedTwice]: ColourNames.red,
  [LineExceptionReasons.despatchedMoreThanOrdered]: ColourNames.red,
  [LineExceptionReasons.invoicedMoreThanOrdered]: ColourNames.red,
  // (g) held at Staged for Cutting
  [LineExceptionReasons.finishedGoodsAtCutting]: ColourNames.teal,
  [LineExceptionReasons.stagedNotAtCutting]: ColourNames.teal,
  // (h) late for delivery (Sales Order Board D-12)
  [LineExceptionReasons.lateForDelivery]: ColourNames.indigo,
};

// A row's colour on the Exceptions list: green when it has no reason. Otherwise its first reason that
// says WHAT is wrong, (b)-(g), in the order the Reason column reads them; then a Traffic Light step, in
// the step's own colour (Manufacturing added to the Exceptions list, D-6); then late for delivery (h);
// the age reason (a) only when it is the line's sole reason, because most lines that are stuck for a
// cause are also old, and the age would otherwise hide the cause (Sales Order Board D-12: a late line
// that is also slow at a status shows as late). A step with no colour, and a code this list does not
// know yet, are passed over; a line with only such codes is left untinted rather than guessed at.
export function lineExceptionColour(reasons: CodeParameters[] | undefined | null): ColourNames | undefined {
  const list = reasons ?? [];
  if (!list.length) {
    return ColourNames.green;
  }
  const colour = (reason: CodeParameters) => lineExceptionReasonColours[reason?.code];
  const general: string[] = [LineExceptionReasons.tooLongAtStatus, LineExceptionReasons.lateForDelivery];
  const cause = list.find((reason) => !general.includes(reason.code) && colour(reason));
  if (cause) {
    return colour(cause);
  }
  const step = list.map((reason) => trafficLightStepColour(reason)).find((stepColour) => !!stepColour);
  if (step) {
    return step;
  }
  const chosen =
    list.find((reason) => reason.code === LineExceptionReasons.lateForDelivery) ??
    list.find((reason) => colour(reason));
  return chosen ? colour(chosen) : undefined;
}

// A Traffic Light step reason's colour: the step's own Colour as stored (ui.md, "Status colours come from the
// DATABASE"). Blank, or anything that is not a ColourNames value, is no colour. Undefined for any other code.
export function trafficLightStepColour(reason: CodeParameters | undefined | null): ColourNames | undefined {
  if (reason?.code !== LineExceptionReasons.trafficLightStep) {
    return undefined;
  }
  const stored = (reason.parameters?.['colour'] ?? '').trim().toLowerCase();
  return stored ? Object.values(ColourNames).find((name) => name.toLowerCase() === stored) : undefined;
}

// Which rule each reason code belongs to - the days, statuses and documents in its words never
// split a rule (D-2).
const lineExceptionReasonRules: Record<string, LineExceptionRules> = {
  [LineExceptionReasons.tooLongAtStatus]: LineExceptionRules.tooLongAtStatus,
  [LineExceptionReasons.madeBeforeCut]: LineExceptionRules.madeBeforeCut,
  [LineExceptionReasons.journeyClosedNothingDespatched]: LineExceptionRules.journeyClosedNothingDespatched,
  [LineExceptionReasons.noLiveList]: LineExceptionRules.pickingListOrTransfer,
  [LineExceptionReasons.deadList]: LineExceptionRules.pickingListOrTransfer,
  [LineExceptionReasons.transferNotDeparted]: LineExceptionRules.pickingListOrTransfer,
  [LineExceptionReasons.productMismatch]: LineExceptionRules.wrongStockAllocated,
  [LineExceptionReasons.warehouseMismatch]: LineExceptionRules.wrongStockAllocated,
  [LineExceptionReasons.despatchedTwice]: LineExceptionRules.despatchedOrInvoicedTooMuch,
  [LineExceptionReasons.despatchedMoreThanOrdered]: LineExceptionRules.despatchedOrInvoicedTooMuch,
  [LineExceptionReasons.invoicedMoreThanOrdered]: LineExceptionRules.despatchedOrInvoicedTooMuch,
  [LineExceptionReasons.finishedGoodsAtCutting]: LineExceptionRules.stagedForCutting,
  [LineExceptionReasons.stagedNotAtCutting]: LineExceptionRules.stagedForCutting,
  [LineExceptionReasons.lateForDelivery]: LineExceptionRules.lateForDelivery,
};

// The rule a reason code belongs to; undefined for a code this screen does not know yet.
export function lineExceptionRule(code: string | undefined | null): LineExceptionRules | undefined {
  return code ? lineExceptionReasonRules[code] : undefined;
}

// The distinct rules a line breaks, in rule order: two codes of one rule count once (D-3).
export function lineExceptionRulesOf(reasons: CodeParameters[] | undefined | null): LineExceptionRules[] {
  const rules = new Set((reasons ?? []).map((reason) => lineExceptionRule(reason?.code)));
  return Object.values(LineExceptionRules).filter((rule) => rules.has(rule));
}

// A rule's colour - the colour its codes give a row on the Exceptions list (D-4).
export function lineExceptionRuleColour(rule: LineExceptionRules): ColourNames | undefined {
  const code = Object.keys(lineExceptionReasonRules).find((key) => lineExceptionReasonRules[key] === rule);
  return code ? lineExceptionReasonColours[code] : undefined;
}

// A rule's tile label on the Sales Order Board (D-2, Mock-ups): the plain reason, with no days,
// statuses or documents.
export function lineExceptionRuleLabel(rule: LineExceptionRules): string {
  switch (rule) {
    case LineExceptionRules.tooLongAtStatus:
      return $localize`Too long at a status`;
    case LineExceptionRules.madeBeforeCut:
      return $localize`Made before fabric cut`;
    case LineExceptionRules.journeyClosedNothingDespatched:
      return $localize`Journey closed, nothing despatched`;
    case LineExceptionRules.pickingListOrTransfer:
      return $localize`Picking list or transfer problem`;
    case LineExceptionRules.wrongStockAllocated:
      return $localize`Wrong stock allocated`;
    case LineExceptionRules.despatchedOrInvoicedTooMuch:
      return $localize`Despatched or invoiced too much`;
    case LineExceptionRules.stagedForCutting:
      return $localize`Staged for Cutting problem`;
    case LineExceptionRules.lateForDelivery:
      return $localize`Late for delivery`;
  }
}

// The audit's verdict (requirement 6): the reason, then - where one helps - what to check or do.
export function lineVerdictText(verdict: CodeParameters): string {
  const parameter = parameterReader(verdict);
  const reason = lineExceptionReasonText(verdict);

  switch (verdict?.code) {
    case LineExceptionReasons.tooLongAtStatus:
      return $localize`${reason}:reason:. Check what is holding the line at this status.`;
    case LineExceptionReasons.journeyClosedNothingDespatched:
    case LineExceptionReasons.noLiveList:
    case LineExceptionReasons.deadList:
    case LineExceptionReasons.stagedNotAtCutting:
      // The rules Un-stick is offered for (D-8, D-15) - it re-derives the status from the facts.
      return $localize`${reason}:reason:. Un-stick works the line's status out again from what has happened.`;
    case LineExceptionReasons.transferNotDeparted:
      return $localize`${reason}:reason:. Check whether the transfer has left.`;
    case LineExceptionReasons.productMismatch:
      return $localize`${reason}:reason:. Check the allocated stock is the right product.`;
    case LineExceptionReasons.warehouseMismatch:
      return $localize`${reason}:reason:. Check where ${parameter('stock')}:stock: really is.`;
    case LineExceptionReasons.despatchedTwice:
    case LineExceptionReasons.despatchedMoreThanOrdered:
      return $localize`${reason}:reason:. Check the line's despatch notes.`;
    case LineExceptionReasons.invoicedMoreThanOrdered:
      return $localize`${reason}:reason:. Check the line's invoices.`;
    case LineExceptionReasons.trafficLightStep:
      // Not un-stickable (D-7): the line moves on when the step is ticked in Manufacturing.
      return $localize`${reason}:reason:. Tick the step on the Traffic Light System once it is done.`;
    case LineExceptionReasons.finishedGoodsAtCutting:
      // The scanner never offers stock standing at a cutting location (D-17), so it has to move.
      return parameter('putAwayLocation')
        ? $localize`${reason}:reason:. Move ${parameter('stock')}:stock: to ${parameter('putAwayLocation')}:putAwayLocation: so it can be picked on the scanner.`
        : $localize`${reason}:reason:. Move ${parameter('stock')}:stock: to its put-away location so it can be picked on the scanner.`;
    default:
      // Made before cut, No problems found, and anything unrecognised: the reason says it all.
      return reason;
  }
}

// Reads a named parameter, blank when the API did not send it.
function parameterReader(codeParameters: CodeParameters | undefined | null): (name: string) => string {
  return (name: string) => codeParameters?.parameters?.[name] ?? '';
}

// The API sends dates as yyyy-MM-dd. Shown as dd/MM/yyyy, what FormatService.date (shortDate, en-GB)
// shows on the other screens. Built from the parts rather than through a Date, so it cannot slip a day
// across time zones, and not through $localize.locale, which gave the US look ("Oct 21, 2026").
function displayDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}
